import React, { useEffect, useRef, useState } from 'react';
import './AttachmentOverlay.css';

type Item = { type: 'image'; url?: string; preview?: string; alt?: string; uploading?: boolean; error?: string };

interface Props {
  attachments: Item[];
  isUploading: boolean;
  onRemove: (index: number) => void;
}

export const AttachmentOverlay: React.FC<Props> = ({ attachments, isUploading, onRemove }) => {
  const [open, setOpen] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Focus the close button when the fullscreen preview opens
  useEffect(() => {
    if (expandedIndex !== null) {
      closeButtonRef.current?.focus();
    }
  }, [expandedIndex]);

  useEffect(() => {
    if (expandedIndex === null) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setExpandedIndex(null);
      }
      // Trap focus within the overlay
      if (event.key === 'Tab' && overlayRef.current) {
        const focusable = overlayRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [expandedIndex]);

  // Show overlay only when there are actual attachments
  if (attachments.length === 0) return null;

  const previewOpen = expandedIndex !== null;

  return (
    <>
      <div className="attachment-overlay">
        <button
          onClick={() => setOpen(o => !o)}
          title={open ? 'Hide attachments' : 'Show attachments'}
          className="attachment-toggle-button"
          aria-expanded={open}
          aria-label={open ? 'Collapse attachment preview' : 'Expand attachment preview'}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`attachment-toggle-icon ${open ? 'open' : ''}`}
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>
        {/* Minimized state: tiny thumbnail strip */}
        {!open && (
          <div className="attachment-minimized">
            {attachments.map((a, i) => (
              <button
                key={i}
                type="button"
                className="attachment-thumb-button"
                onClick={() => setExpandedIndex(i)}
                aria-label={`Open attachment ${i + 1} in full screen`}
              >
                <img src={a.url || a.preview} alt={a.alt || ''} className="attachment-thumbnail" />
              </button>
            ))}
          </div>
        )}

        {open && (
          <div className="attachment-expanded">
            {attachments.map((a, i) => (
              <div key={i} className="attachment-item">
                <button
                  type="button"
                  className="attachment-image-button"
                  onClick={() => setExpandedIndex(i)}
                  aria-label={`Expand attachment ${i + 1}`}
                >
                  <img
                    src={a.url || a.preview}
                    alt={a.alt || ''}
                    className="attachment-image"
                  />
                </button>
                <button
                  type="button"
                  onClick={() => onRemove(i)}
                  title="Delete attachment"
                  className="attachment-remove-button"
                  aria-label={`Delete attachment ${i + 1}`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon feather feather-trash-2"
                  >
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    <line x1="10" y1="11" x2="10" y2="17"></line>
                    <line x1="14" y1="11" x2="14" y2="17"></line>
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
        {isUploading && (
          <div className="attachment-uploading-indicator" aria-live="polite">
            Uploading…
          </div>
        )}
      </div>

      <div
        ref={overlayRef}
        className={`attachment-fullscreen-overlay${previewOpen ? ' is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Attachment preview"
        onClick={() => setExpandedIndex(null)}
      >
        {expandedIndex !== null && attachments[expandedIndex] && (
          <div className="attachment-fullscreen-content" onClick={(event) => event.stopPropagation()}>
            <button
              ref={closeButtonRef}
              type="button"
              className="attachment-fullscreen-close"
              onClick={() => setExpandedIndex(null)}
              aria-label="Close full screen attachment preview"
            >
              ×
            </button>
            <img
              src={attachments[expandedIndex].url || attachments[expandedIndex].preview}
              alt={attachments[expandedIndex].alt || ''}
              className="attachment-fullscreen-image"
              onClick={() => setExpandedIndex(null)}
            />
          </div>
        )}
      </div>
    </>
  );
};
